"use client";

import React, { useRef, useState, useEffect } from "react";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";

// local component imports
import { FullscreenIcon, MuteIcon, UnmuteIcon, ShareIcon, PlayIcon, PauseIcon } from "./Icons";
import MobileFullScreen from "./MobileFullScreen";
import DesktopFullScreen from "./DesktopFullScreen";
import VideoPlayer from "../../../../../../components/atoms/videoPlayer";

// services and utils imports
import { useWidgetClick } from "../../../../../../utils/dynamicHomeUtils/WidgetClickContext";

// lazy loaded components
const ProductCarousel = dynamic(() => import("../ProductCarousel"), { ssr: false });

/**
 * VideoCard renders a single video card with play/pause, mute, share, and fullscreen controls.
 * @param {object} props - Component props
 * @param {object} props.video - Video data object
 * @param {boolean} props.isPlaying - Whether this video is currently playing
 * @param {boolean} props.isWidgetInView - Whether the parent widget has entered the viewport
 * @param {Function} props.onPlay - Callback to start playing this video
 * @param {Function} props.onPause - Callback to pause this video
 * @param {Array} props.allVideos - All videos (for fullscreen carousel context)
 * @param {number} props.videoIndex - Index of this video in allVideos
 * @returns {React.ReactElement} The rendered video card
 */
const VideoCard = ({
  video,
  isPlaying,
  isWidgetInView = false,
  onPlay,
  onPause,
  allVideos = [],
  videoIndex = 0,
  products = [],
  isLoadingProducts = false,
  videoProducts = [],
}) => {
  const { onCellClick } = useWidgetClick() ?? {};
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [showReels, setShowReels] = useState(false);
  const [showDesktopModal, setShowDesktopModal] = useState(false);

  const buttonText = video?.button?.text;

  useEffect(() => {
    if (!isWidgetInView || !videoRef.current) {
      return;
    }
    if (isPlaying) {
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [isPlaying, isWidgetInView]);

  /**
   * Starts playing the video on mouse enter.
   */
  const onMouseEnter = () => onPlay();

  /**
   * Pauses the video on mouse leave.
   */
  const onMouseLeave = () => onPause();

  /**
   * Fires Widget Clicked for video engagement (play / controls).
   * @param {string} [interactionLabel] - Optional label for the interaction
   * @returns {void}
   */
  const fireVideoWidgetClick = (interactionLabel) => {
    onCellClick?.({
      action: "Video Clicked",
      item: {
        item_name: interactionLabel || video.title || "Video",
        item_position: videoIndex,
        item_row: 1,
        item_column: (videoIndex ?? 0) + 1,
        redirection_url: video.videoUrl,
      },
    });
  };

  /**
   * Toggles play/pause state.
   */
  const onTogglePlay = () => {
    fireVideoWidgetClick(video.title || "Play toggle");
    if (isPlaying) {
      onPause();
    } else {
      onPlay();
    }
  };

  /**
   * Toggles mute state on the video element.
   */
  const onToggleMute = () => {
    fireVideoWidgetClick("Mute toggle");
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  /**
   * Opens the appropriate fullscreen view based on device type.
   */
  const onFullscreen = () => {
    fireVideoWidgetClick("Fullscreen");
    const isMobileViewport = window.innerWidth < 768;
    onPause();
    if (isMobileViewport && allVideos.length > 0) {
      setShowReels(true);
    } else {
      setShowDesktopModal(true);
    }
  };

  /**
   * Closes the mobile reels overlay.
   */
  const onCloseReels = () => setShowReels(false);

  /**
   * Closes the desktop fullscreen modal.
   */
  const onCloseDesktopModal = () => setShowDesktopModal(false);

  /**
   * Shares the current page URL via the Web Share API.
   */
  const onShare = () => {
    fireVideoWidgetClick("Share");
    navigator.share?.({ url: window.location.href });
  };

  /**
   * Navigates to the footer CTA URL.
   */
  const onFooterCtaClick = () => {
    onCellClick?.({
      action: "CTA Clicked",
      item: {
        item_name: video?.footer?.text || "Video footer CTA",
        redirection_url: video?.footer?.linkUrl,
      },
    });
    if (video?.footer?.linkUrl) {
      window.open(video.footer.linkUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className="bg-black relative w-full cursor-pointer overflow-hidden rounded-2xl md:max-h-none"
      style={{ aspectRatio: "2/3" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isWidgetInView ? (
        <VideoPlayer
          customRef={videoRef}
          videoUrl={video.videoUrl}
          poster={video.thumbnail}
          title={video.title || "Video"}
          classes="h-full w-full object-cover"
          isMuted={isMuted}
          isLoop={true}
          isPlaysInline={true}
          preload="auto"
          isAutoPlay={false}
        />
      ) : (
        video.thumbnail && (
          <img
            src={video.thumbnail}
            alt={video.title || "Video thumbnail"}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        )
      )}
      <div className="absolute left-[10px] right-[10px] top-[10px] flex items-center justify-between">
        <button
          type="button"
          onClick={onFullscreen}
          aria-label="Enter fullscreen"
          className="flex h-[36px] w-[36px] items-center justify-center rounded-[35.1px] bg-[rgba(0,0,0,0.20)]"
        >
          <FullscreenIcon />
        </button>
        <div className="flex gap-[10px]">
          <button
            type="button"
            onClick={onToggleMute}
            aria-label={isMuted ? "Unmute" : "Mute"}
            className="flex h-[36px] w-[36px] items-center justify-center rounded-[35.1px] bg-[rgba(0,0,0,0.20)]"
          >
            {isMuted ? <MuteIcon /> : <UnmuteIcon />}
          </button>
          <button
            type="button"
            onClick={onShare}
            aria-label="Share video"
            className="flex h-[36px] w-[36px] items-center justify-center rounded-[35.1px] bg-[rgba(0,0,0,0.20)] p-2"
          >
            <ShareIcon />
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={onTogglePlay}
        aria-label={isPlaying ? "Pause video" : "Play video"}
        className="absolute left-1/2 top-1/2 flex h-[48px] w-[48px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-[4px] rounded-[24px] bg-[rgba(15,23,43,0.24)] p-[4px_8px]"
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </button>

      {video.showCta || products.length > 0 ? (
        <div
          className="absolute bottom-0 left-0 right-0 pb-3 pl-3 pr-3 pt-[60px]"
          style={{ background: "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.80) 100%)" }}
        >
          {video.showCta && video.footer ? (
            <button
              type="button"
              onClick={onFooterCtaClick}
              className="mx-[10px] mb-[10px] flex w-[calc(100%-20px)] items-center justify-center gap-[5px] rounded-xl px-4 py-3"
              style={{
                backgroundColor: video.footer.backgroundColor || "#ffffff",
                color: video.footer.textColor || "#1A1A1A",
                borderRadius: video.footer.borderRadius ?? 12,
                border: `${video.footer.borderThickness ?? 0.5}px solid ${video.footer.borderColor || "#1A1A1A"}`,
              }}
            >
              <span className="text-sm font-semibold">{video.footer.text || buttonText}</span>
              {video.footer.showArrow && <span>&#8250;</span>}
            </button>
          ) : (
            <ProductCarousel products={products} isLoading={isLoadingProducts} />
          )}
        </div>
      ) : null}

      {showReels && (
        <MobileFullScreen
          videos={allVideos}
          initialIndex={videoIndex}
          onClose={onCloseReels}
          videoProducts={videoProducts}
        />
      )}

      {showDesktopModal && (
        <DesktopFullScreen
          videos={allVideos}
          initialIndex={videoIndex}
          onClose={onCloseDesktopModal}
          videoProducts={videoProducts}
        />
      )}
    </div>
  );
};

VideoCard.propTypes = {
  video: PropTypes.shape({
    videoUrl: PropTypes.string,
    thumbnail: PropTypes.string,
    title: PropTypes.string,
    taggedProducts: PropTypes.arrayOf(PropTypes.object),
    showCta: PropTypes.bool,
    footer: PropTypes.shape({
      text: PropTypes.string,
      linkUrl: PropTypes.string,
      showArrow: PropTypes.bool,
      backgroundColor: PropTypes.string,
      textColor: PropTypes.string,
      borderColor: PropTypes.string,
      borderRadius: PropTypes.number,
      borderThickness: PropTypes.number,
    }),
    button: PropTypes.shape({
      text: PropTypes.string,
      url: PropTypes.string,
    }),
  }).isRequired,
  isPlaying: PropTypes.bool.isRequired,
  isWidgetInView: PropTypes.bool,
  onPlay: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  allVideos: PropTypes.arrayOf(PropTypes.object),
  videoIndex: PropTypes.number,
  products: PropTypes.arrayOf(PropTypes.object),
  isLoadingProducts: PropTypes.bool,
  videoProducts: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)),
};

export default VideoCard;
